import React, { useState, useEffect, useRef } from 'react';
import { FetchPolicy } from '@apollo/client';
import * as Types from '@xingternal/pages-graphql-types';

import { usePageContext } from '@xingternal/pages-common';
import {
  trackCommboxAudienceSelect,
  trackCommboxOpenActorSwitch,
  trackCommboxOpenAudienceSelection,
} from '@xingternal/pages-tracking';

import {
  Commbox,
  ATTACHMENT_TYPES,
  COMMBOX_ACTOR_TYPES,
} from '@xingternal/commbox';

import FEED_QUERY from '../FeedQuery.graphql';
import { EntityPageFeedQueryVariables } from '../FeedQuery.gql-types';
import { EntityPageFeedCollectionFragment } from '../EntityPageFeedCollectionFragment.gql-types';

import {
  useCreateTextPostingMutation,
  CreateTextPostingMutationHookResult,
  CreateTextPostingMutationVariables,
} from './CreatePostingMutation.gql-types';
import {
  useUpdatePostingMutation,
  UpdatePostingMutationHookResult,
  UpdatePostingMutationVariables,
} from './UpdatePostingMutation.gql-types';
import CREATE_VIDEO_MUTATION from './CreateVideoMutation.graphql';

import { CommboxOmTrigger } from './index';
import { COMMBOX_SELECTABLE_AUDIENCES, PAID_PLUS, PAID } from '../constants';
import {
  mapAudienceTrackingValue,
} from '../utils';

const COMMBOX_SUCESS_DIALOG_VISIBILITY_TIMEOUT = 7000;

type NewsCommboxProps = {
  canEditLinkPreview: boolean;
  children: React.ReactNode;
  feedQueryVars: EntityPageFeedQueryVariables;
  clearPosting?: () => void;
  defaultPostVisibility?: Types.PostingsVisibility;
  refetch?: () => void;
  selectedPosting?: EntityPageFeedCollectionFragment['object'] | null;
  setLazyLoadingIsAllowed?: (allowed: boolean) => void;
  setSelectedPosting?: (
    posting: EntityPageFeedCollectionFragment['object'] | null
  ) => void;
};

const NewsCommbox = ({
  canEditLinkPreview,
  children,
  clearPosting,
  defaultPostVisibility,
  feedQueryVars,
  refetch,
  selectedPosting,
  setLazyLoadingIsAllowed,
  setSelectedPosting,
}: NewsCommboxProps) => {
  const { pageContext } = usePageContext();
  const [isUploadModalTriggered, setUploadModalTrigger] = useState(false);
  const [postWasSent, setPostWasSent] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isAllowedToPost, setIsAllowedPost] = useState(true);
  const [isVideoUploadOpen, setIsVideoUploadOpen] = useState(false);
  const [attachmentType, setAttachmentType] = useState(ATTACHMENT_TYPES.EMPTY);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [successPostHeadLine, setSuccessPostHeadLine] = useState(
    'COMMBOX_SUCCESS_HEADLINE'
  );
  const [successPostHeadLineValues, setSuccessPostHeadLineValues] = useState(
    {}
  );
  const [successPostDescription, setSuccessPostDescription] = useState(
    'COMMBOX_SUCCESS_DESC'
  );
  const [
    successPostDescriptionValues,
    setSuccessPostDescriptionValues,
  ] = useState({});

  const isPostingSelected =
    selectedPosting !== null && selectedPosting !== undefined;

  const feedQuery = {
    query: FEED_QUERY,
    variables: { ...feedQueryVars },
    fetchPolicy: 'network-only' as FetchPolicy,
  };
  const {
    contractType,
    focusType,
    companyId,
    globalId,
    logo,
    pageTitle,
  } = pageContext;
  const isPaidPage = contractType === PAID_PLUS || contractType === PAID;
  const isNewsPage = (type: Types.EntityPageFocusType) =>
    type === 'TOPIC_PAGE' || type === 'PUBLISHER' || type === 'INDUSTRY_PAGE';

  const [
    postingsCreatePosting,
    { loading: isCreatingPosting, error: createPostingError },
  ] = useCreateTextPostingMutation({
    onCompleted: (data) => {
      if (data.postingsCreatePosting && !data.postingsCreatePosting.error) {
        setShowSuccessMessage(true);
        setPostWasSent(true);
        setLazyLoadingIsAllowed && setLazyLoadingIsAllowed(true);
        if (clearPosting) clearPosting();
        if (refetch) refetch();
        return data;
      }
      return null;
    },
    onError: () => setIsAllowedPost(true),
    refetchQueries: () => [feedQuery],
  });

  const [
    postingUpdatePosting,
    { loading: isUpdatingPosting, error: updatePostingError },
  ] = useUpdatePostingMutation({
    onCompleted: (data) => {
      if (data.postingsUpdatePosting && !data.postingsUpdatePosting.error) {
        setShowSuccessMessage(true);
        setPostWasSent(true);
        if (clearPosting) clearPosting();
        if (refetch) refetch();
        return data;
      }
      return null;
    },
    onError: () => setIsAllowedPost(true),
    refetchQueries: () => [feedQuery],
  });

  const setBannerWhenScheduling = (publishAt: string) => {
    let successMessageValues = {};
    let successMessage = 'COMMBOX_SUCCESS_DESC';
    if (publishAt) {
      setSuccessPostHeadLine('COMMBOX_SUCCESS_HEADLINE');
      setSuccessPostHeadLineValues({});
      successMessage = 'COMMBOX_SUCCESS_SCHEDULED_DESC';
      const publishedDate = new Date(publishAt);
      successMessageValues = {
        date: `${publishedDate.getDate()}/${
          publishedDate.getMonth() + 1
        }/${publishedDate.getFullYear()}`,
        time: `${publishedDate.getHours()}:${
          publishedDate.getMinutes() < 10
            ? `0${publishedDate.getMinutes()}`
            : publishedDate.getMinutes()
        }`,
      };
      setSuccessPostDescription(successMessage);
      setSuccessPostDescriptionValues(successMessageValues);
    }
  };

  const createPosting = (
    mutation: CreateTextPostingMutationHookResult[0]
  ) => async ({
    comment,
    commentArticleV1,
    actorGlobalId,
    images = null,
    links = null,
    publishAt = null,
    publicationStrategy = Types.PostingPublicationStrategy.Now,
    videos = null,
    visibility = defaultPostVisibility,
  }: CreateTextPostingMutationVariables) => {
    setBannerWhenScheduling(publishAt);
    const { data } = await mutation({
      variables: {
        actorGlobalId,
        comment: comment && comment.length ? comment : '',
        commentArticleV1,
        images,
        links,
        publishAt,
        publicationStrategy,
        videos,
        visibility,
      },
    });
    return data?.postingsCreatePosting;
  };

  const updatePosting = (
    mutation: UpdatePostingMutationHookResult[0]
  ) => async ({
    id,
    commentArticleV1,
    publishAt,
  }: UpdatePostingMutationVariables) => {
    setBannerWhenScheduling(publishAt);
    const { data } = await mutation({
      variables: {
        id,
        commentArticleV1,
        publishAt,
      },
    });
    return data?.postingsUpdatePosting;
  };

  const actors = [
    {
      logo: logo as string,
      actorName: pageTitle,
      globalId,
      type: COMMBOX_ACTOR_TYPES.PAGE,
      features: [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO],
    },
  ];

  useEffect(() => {
    if (postWasSent) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      timeoutRef.current = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessPostDescription('COMMBOX_SUCCESS_DESC');
        setSuccessPostDescriptionValues({});
        timeoutRef.current = null;
      }, COMMBOX_SUCESS_DIALOG_VISIBILITY_TIMEOUT);
      setPostWasSent(false);
    }
  }, [postWasSent]);

  useEffect(
    () => () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    },
    []
  );

  return (
    <Commbox
      {...{
        attachmentType,
        canEditLinkPreview,
        globalId,
        isAllowedToPost,
        isUploadModalTriggered,
        isVideoUploadOpen,
        setAttachmentType,
        setIsAllowedPost,
        setIsVideoUploadOpen,
        setShowSuccessMessage,
        setUploadModalTrigger,
        showSuccessMessage,
        successPostDescription,
        successPostDescriptionValues,
        successPostHeadLine,
        successPostHeadLineValues,
      }}
      successMessage={{
        description: successPostDescription,
        descriptionValues: successPostDescriptionValues,
        headline: successPostHeadLine,
        headlineValues: successPostHeadLineValues,
        setShowSuccessMessage,
        showSuccessMessage,
      }}
      featurePermissions={{
        enableMentions: true,
        enableVideo: true,
        enableScheduling: isPaidPage,
        enablePolls: false,
        enableMultiImage: false,
        enableSchedulingLock: Boolean(companyId) || isNewsPage(focusType),
      }}
      application="ENTITY_PAGES"
   
      createVideoMutation={CREATE_VIDEO_MUTATION}
      createPostingError={
        isPostingSelected ? updatePostingError : createPostingError
      }
      isCreatingPosting={
        isPostingSelected ? isUpdatingPosting : isCreatingPosting
      }
      onPost={
        isPostingSelected
          ? updatePosting(postingUpdatePosting)
          : createPosting(postingsCreatePosting)
      }
      onOpenAudienceSelection={() => trackCommboxOpenAudienceSelection()}
      onOpenActorSwitch={() => trackCommboxOpenActorSwitch()}
      onSelectAudience={(id: 'PUBLIC' | 'PRIVATE') =>
        trackCommboxAudienceSelect(mapAudienceTrackingValue(id))
      }
      onClose={() => {
        if (clearPosting) clearPosting();
        setIsVideoUploadOpen(false);
      }}
      pageId={globalId}
      posting={selectedPosting}
      actors={actors}
      selectableAudiences={COMMBOX_SELECTABLE_AUDIENCES}
    >
      {children ||
        ((onClick: () => void) => (
          <CommboxOmTrigger
            onClick={() => {
              setSelectedPosting && setSelectedPosting(null);
              onClick();
            }}
          />
        ))}
    </Commbox>
  );
};

export { NewsCommbox };
